import React, { useDeferredValue, useEffect, useRef, useState } from 'react';
import {
  ExternalLink,
  Link2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { catalogApi } from '../../../services/catalogApi';
import type {
  CatalogEntity,
  CatalogEvent,
  CatalogEventDetail,
  CatalogEventInput,
  EventSourceEventLink,
} from '../../../types/catalog';
import { displayEntityType } from '../../../utils/entityTerminology';
import {
  ButtonRow,
  Field,
  Input,
  Label,
  PrimaryButton,
  SecondaryButton,
} from '../AdminUI';
import { EventForm } from './CatalogForms';
import {
  ActionRow,
  Badge,
  CompactActionButton,
  CompactActions,
  DataTable,
  DetailHeader,
  DetailPane,
  DetailSubtitle,
  DetailTitle,
  EmptyState,
  ErrorBanner,
  Hint,
  InfoItem,
  InfoStrip,
  ListPane,
  Loading,
  PaneToolbar,
  ResultButton,
  ResultList,
  ResultMeta,
  ResultTitle,
  ResourceContent,
  ResourceDescription,
  ResourceList,
  ResourceMeta,
  ResourceRow,
  ResourceTitle,
  SearchInput,
  SearchRow,
  SearchSubmit,
  Section,
  SectionHeader,
  SectionTitle,
  SplitPane,
  TableWrap,
  formatDate,
  errorMessage,
} from './CatalogUI';

interface EventManagerProps {
  focusEventId?: number | null;
  onOpenEntity: (entityId: number) => void;
  onOpenSource: (eventSourceId: number) => void;
}

interface SourceGroup {
  source: EventSourceEventLink;
  entities: string[];
}

function groupSources(rows: EventSourceEventLink[]): SourceGroup[] {
  const groups = new Map<number, SourceGroup>();
  rows.forEach((row) => {
    const current = groups.get(row.eventSourceId) ?? { source: row, entities: [] };
    if (row.entityName && !current.entities.includes(row.entityName)) {
      current.entities.push(row.entityName);
    }
    groups.set(row.eventSourceId, current);
  });
  return Array.from(groups.values());
}

export const EventManager: React.FC<EventManagerProps> = ({
  focusEventId,
  onOpenEntity,
  onOpenSource,
}) => {
  const [search, setSearch] = useState('');
  const [events, setEvents] = useState<CatalogEvent[]>([]);
  const [pastDays, setPastDays] = useState(30);
  const [selected, setSelected] = useState<CatalogEventDetail | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [linkingEntity, setLinkingEntity] = useState(false);
  const [entitySearch, setEntitySearch] = useState('');
  const deferredEntitySearch = useDeferredValue(entitySearch);
  const entitySearchSequence = useRef(0);
  const [entitySearchRefresh, setEntitySearchRefresh] = useState(0);
  const [entitySearchLoading, setEntitySearchLoading] = useState(false);
  const [entityResults, setEntityResults] = useState<CatalogEntity[]>([]);

  const loadEvents = async (historyDays = pastDays) => {
    setLoadingList(true);
    setError(null);
    try {
      setEvents(await catalogApi.listEvents({ search, pastDays: historyDays, limit: 100 }));
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoadingList(false);
    }
  };

  const chooseEvent = async (eventId: number) => {
    setLoadingDetail(true);
    setError(null);
    setEditing(false);
    setLinkingEntity(false);
    try {
      const detail = await catalogApi.getEvent(eventId);
      setSelected(detail);
      setEvents((current) => current.some((item) => item.eventId === detail.event.eventId)
        ? current.map((item) => item.eventId === detail.event.eventId ? detail.event : item)
        : [detail.event, ...current]);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    let active = true;
    catalogApi.listEvents({ pastDays: 30, limit: 100 })
      .then((result) => {
        if (active) setEvents(result);
      })
      .catch((loadError) => {
        if (active) setError(errorMessage(loadError));
      })
      .finally(() => {
        if (active) setLoadingList(false);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!focusEventId || selected?.event.eventId === focusEventId) return;
    chooseEvent(focusEventId);
    // focusEventId is the navigation signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusEventId]);

  useEffect(() => {
    const query = deferredEntitySearch.trim();
    if (!linkingEntity || query.length < 2) {
      setEntityResults([]);
      setEntitySearchLoading(false);
      return;
    }

    let active = true;
    const sequence = ++entitySearchSequence.current;
    const timer = window.setTimeout(async () => {
      setEntitySearchLoading(true);
      try {
        const result = await catalogApi.listEntities(query, 30);
        if (active && sequence === entitySearchSequence.current) setEntityResults(result);
      } catch (searchError) {
        if (active && sequence === entitySearchSequence.current) setError(errorMessage(searchError));
      } finally {
        if (active && sequence === entitySearchSequence.current) setEntitySearchLoading(false);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [deferredEntitySearch, entitySearchRefresh, linkingEntity]);

  const submitSearch = async (event: React.FormEvent) => {
    event.preventDefault();
    await loadEvents(pastDays);
  };

  const saveEvent = async (input: CatalogEventInput) => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await catalogApi.updateEvent(selected.event.eventId, input);
      await chooseEvent(selected.event.eventId);
      setEditing(false);
    } catch (saveError) {
      setError(errorMessage(saveError));
    } finally {
      setBusy(false);
    }
  };

  const findEntities = (event: React.FormEvent) => {
    event.preventDefault();
    setEntitySearchRefresh((current) => current + 1);
  };

  const linkEntity = async (entity: CatalogEntity) => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await catalogApi.linkEntityEvent(entity.entityId, selected.event.eventId);
      setLinkingEntity(false);
      setEntityResults([]);
      setEntitySearch('');
      await chooseEvent(selected.event.eventId);
    } catch (linkError) {
      setError(errorMessage(linkError));
    } finally {
      setBusy(false);
    }
  };

  const unlinkEntity = async (entityId: number, entityName: string, origin: string) => {
    if (!selected) return;
    const detail = origin === 'source'
      ? ' This creates an event-only exclusion and does not remove the entity from its source.'
      : '';
    if (!window.confirm(`Remove ${entityName} from this event?${detail}`)) return;
    setBusy(true);
    setError(null);
    try {
      await catalogApi.unlinkEntityEvent(entityId, selected.event.eventId);
      await chooseEvent(selected.event.eventId);
    } catch (unlinkError) {
      setError(errorMessage(unlinkError));
    } finally {
      setBusy(false);
    }
  };

  const sourceGroups = groupSources(selected?.eventSources ?? []);

  return (
    <SplitPane>
      <ListPane>
        <PaneToolbar>
          <SearchRow onSubmit={submitSearch}>
            <SearchInput
              aria-label="Search events"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search title, description, or URL"
            />
            <SearchSubmit label="Search events" disabled={loadingList} />
          </SearchRow>
          <ButtonRow>
            <Field style={{ margin: 0, width: 130 }}>
              <Label htmlFor="events-past-days">Past days</Label>
              <Input
                id="events-past-days"
                type="number"
                min={0}
                max={3650}
                value={pastDays}
                onChange={(event) => setPastDays(Number(event.target.value))}
              />
            </Field>
            <SecondaryButton type="button" onClick={() => loadEvents(pastDays)}>
              <RefreshCw size={16} /> Apply
            </SecondaryButton>
          </ButtonRow>
        </PaneToolbar>
        {loadingList ? <Loading label="Loading events" /> : (
          <ResultList>
            {events.length === 0 && <EmptyState>No events found.</EmptyState>}
            {events.map((catalogEvent) => (
              <ResultButton
                type="button"
                key={catalogEvent.eventId}
                $selected={selected?.event.eventId === catalogEvent.eventId}
                onClick={() => chooseEvent(catalogEvent.eventId)}
              >
                <ResultTitle>{catalogEvent.title}</ResultTitle>
                <ResultMeta>
                  <span>{formatDate(catalogEvent.startDate)}</span>
                  <Badge>{catalogEvent.isVirtual ? 'Virtual' : 'In person'}</Badge>
                  <span>#{catalogEvent.eventId}</span>
                </ResultMeta>
              </ResultButton>
            ))}
          </ResultList>
        )}
      </ListPane>

      <DetailPane>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        {!selected && !loadingDetail ? (
          <EmptyState>Select an event to edit it or manage entity relationships.</EmptyState>
        ) : loadingDetail ? (
          <Loading label="Loading event relationships" />
        ) : selected && (
          <>
            <DetailHeader>
              <div>
                <DetailTitle>{selected.event.title}</DetailTitle>
                <DetailSubtitle>
                  {formatDate(selected.event.startDate)} · Event #{selected.event.eventId}
                </DetailSubtitle>
              </div>
              <ActionRow>
                <SecondaryButton type="button" onClick={() => setEditing(!editing)}>
                  <Pencil size={16} /> Edit event
                </SecondaryButton>
                <SecondaryButton
                  type="button"
                  onClick={() => chooseEvent(selected.event.eventId)}
                >
                  <RefreshCw size={16} /> Refresh
                </SecondaryButton>
              </ActionRow>
            </DetailHeader>

            <InfoStrip aria-label="Event information">
              <InfoItem>
                <dt>Type</dt>
                <dd>{selected.event.type || 'Not set'}</dd>
              </InfoItem>
              <InfoItem>
                <dt>Schedule</dt>
                <dd>{formatDate(selected.event.startDate)} - {formatDate(selected.event.endDate)}</dd>
              </InfoItem>
              <InfoItem>
                <dt>Format</dt>
                <dd>{selected.event.isVirtual ? 'Virtual' : 'In person'}</dd>
              </InfoItem>
              <InfoItem>
                <dt>Location</dt>
                <dd>
                  {selected.event.isVirtual
                    ? 'Online'
                    : [selected.event.venue, selected.event.city, selected.event.state, selected.event.country]
                      .filter(Boolean).join(', ') || 'Not set'}
                </dd>
              </InfoItem>
              {selected.event.eventUrl && (
                <InfoItem>
                  <dt>Event URL</dt>
                  <dd>{selected.event.eventUrl}</dd>
                </InfoItem>
              )}
            </InfoStrip>
            {selected.event.description && (
              <ResourceDescription>{selected.event.description}</ResourceDescription>
            )}

            {editing && (
              <Section>
                <SectionHeader><SectionTitle>Event fields</SectionTitle></SectionHeader>
                <EventForm event={selected.event} busy={busy} onSubmit={saveEvent} />
              </Section>
            )}

            <Section>
              <SectionHeader>
                <SectionTitle>Linked entities ({selected.entities.length})</SectionTitle>
                <PrimaryButton type="button" onClick={() => setLinkingEntity(!linkingEntity)}>
                  <Plus size={16} /> Link entity
                </PrimaryButton>
              </SectionHeader>

              {linkingEntity && (
                <Section>
                  <SearchRow onSubmit={findEntities}>
                    <SearchInput
                      aria-label="Search entities to link to event"
                      aria-autocomplete="list"
                      aria-controls="event-entity-suggestions"
                      aria-expanded={entityResults.length > 0}
                      role="combobox"
                      value={entitySearch}
                      onChange={(event) => {
                        setEntitySearch(event.target.value);
                        setEntityResults([]);
                        setEntitySearchLoading(event.target.value.trim().length >= 2);
                      }}
                      placeholder="Start typing an entity name"
                    />
                    <SearchSubmit label="Search entities" disabled={entitySearchLoading} />
                  </SearchRow>
                  <Hint aria-live="polite">
                    {entitySearchLoading
                      ? 'Finding entities...'
                      : entitySearch.trim().length < 2
                        ? 'Type at least 2 characters'
                        : `${entityResults.length} suggestions`}
                  </Hint>
                  {entityResults.length > 0 && (
                    <TableWrap id="event-entity-suggestions">
                      <DataTable>
                        <thead><tr><th>Entity</th><th>Type</th><th>Action</th></tr></thead>
                        <tbody>
                          {entityResults.map((entity) => {
                            const linked = selected.entities.some((item) => item.entityId === entity.entityId);
                            return (
                              <tr key={entity.entityId}>
                                <td>{entity.entityName}</td>
                                <td>{displayEntityType(entity.entityType)}</td>
                                <td>
                                  <SecondaryButton
                                    type="button"
                                    disabled={linked || busy}
                                    onClick={() => linkEntity(entity)}
                                  >
                                    <Link2 size={15} /> {linked ? 'Linked' : 'Link'}
                                  </SecondaryButton>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </DataTable>
                    </TableWrap>
                  )}
                </Section>
              )}

              {selected.entities.length === 0 ? <EmptyState>No entities linked.</EmptyState> : (
                <ResourceList>
                  {selected.entities.map((entity) => (
                    <ResourceRow key={entity.entityId}>
                      <ResourceContent>
                        <ResourceTitle>{entity.entityName}</ResourceTitle>
                        <ResourceMeta>
                          <Badge>{displayEntityType(entity.entityType)}</Badge>
                            <Badge $tone={entity.linkOrigin === 'manual' ? 'info' : 'neutral'}>
                              {entity.linkOrigin === 'manual' ? 'Direct' : 'From source'}
                            </Badge>
                          <span>{entity.sourceCount} linked sources</span>
                          <span>Entity #{entity.entityId}</span>
                        </ResourceMeta>
                      </ResourceContent>
                      <CompactActions>
                        <CompactActionButton type="button" onClick={() => onOpenEntity(entity.entityId)}>
                          <ExternalLink size={14} /> Open entity
                        </CompactActionButton>
                        <CompactActionButton
                          type="button"
                          $tone="danger"
                          onClick={() => unlinkEntity(
                            entity.entityId,
                            entity.entityName,
                            entity.linkOrigin
                          )}
                          disabled={busy}
                        >
                          <Trash2 size={14} /> Remove
                        </CompactActionButton>
                      </CompactActions>
                    </ResourceRow>
                  ))}
                </ResourceList>
              )}
            </Section>

            <Section>
              <SectionHeader>
                <SectionTitle>Source provenance ({sourceGroups.length})</SectionTitle>
              </SectionHeader>
              {sourceGroups.length === 0 ? <EmptyState>No source mappings.</EmptyState> : (
                <ResourceList>
                  {sourceGroups.map(({ source, entities: sourceEntities }) => (
                    <ResourceRow key={source.eventSourceId}>
                      <ResourceContent>
                        <ResourceTitle>{source.source}</ResourceTitle>
                        {source.sourceInfo && (
                          <ResourceDescription>{source.sourceInfo}</ResourceDescription>
                        )}
                        <ResourceMeta>
                          {source.eventSourceType !== 'Website' && <Badge>{source.eventSourceType}</Badge>}
                          <span>{sourceEntities.join(', ') || 'Unassigned'}</span>
                          <span>{source.matchScore == null ? 'Not scored' : `Match ${source.matchScore.toFixed(2)}`}</span>
                          <span>Source #{source.eventSourceId}</span>
                        </ResourceMeta>
                      </ResourceContent>
                      <CompactActions>
                        <CompactActionButton type="button" onClick={() => onOpenSource(source.eventSourceId)}>
                          <ExternalLink size={14} /> Open source
                        </CompactActionButton>
                      </CompactActions>
                    </ResourceRow>
                  ))}
                </ResourceList>
              )}
            </Section>
          </>
        )}
      </DetailPane>
    </SplitPane>
  );
};